//@flow
import * as Immutable from 'immutable';

import {
    TaskVisit,
    ChosenLocation,
    Contact,
    Volume,
    Payments,
    TaskSlot
} from 'common/models/flow/TaskVisit';
import {
    validatable,
    validate,
    validateAll
} from 'common/utils/ValidationUtil';
import NumberUtils from 'common/utils/NumberUtils';
import StringUtils from 'common/utils/StringUtils';
import DataUtils from 'common/utils/DataUtils';
import moment from 'moment-timezone';
import {
    ExchangeTypeEnum,
    VisitTypeEnum,
    VisitSourceEnum,
    VisitStatusEnum,
    AppFieldEnums,
    CreateTaskVisitTypesEnum,
    BulkAddableVisitFieldsEnum,
    ConfidenceEnum,
    UserVisitTypeEnum,
    BreakTypeEnum,
    PodTypeEnums
} from 'common/enums/VisitEnums';
import { dashboardColors } from 'common/enums/ColorEnum';

import type {
    TaskVisitModelType,
    ChosenLocationModelType,
    ContactModelType,
    VolumeModelType,
    PaymentsModelType,
    TaskSlotModelType
} from 'common/models/flow/TaskVisit';
import type {
    VisitTypeEnum as VisitTypeEnumFlow,
    ExchangeTypeEnum as ExchangeTypeEnumFlow
} from 'common/models/flow/Plan';

import { StructuredAddress } from './StructuredAddress';
import { Geometry } from './Geometry';
import { VisitSlot } from './VisitSlot';
import { OrderDetail } from './OrderDetail';
import { AmountTransaction } from './AmountTransaction';

import { Amount } from './Amount';

import { Payment } from './Payment';
import { PaymentInstrument } from './PaymentInstrument';

import { LocationId } from './LocationId';

import I18nHelper from 'common/helpers/I18nHelper';
import CountryHelper from 'common/helpers/CountryHelper';

import {
    AppFields,
    AssignedUser,
    TimeDistancePair,
    ConstraintViolation
} from './Plan';
import { TimeSlot } from './TimeSlot';
import { MasterLocation } from './MasterLocation';
import { TourDay } from './TourDay';
import { VisitResource } from './VisitResource';
import { ZoneMapGeometry } from './ZoneMapGeometry';
import { Skills } from './Skills';
import { TaskStatusUpdate } from './StatusUpdate';
import { Team } from './Team';
import { TaskChecklists, SelectedChecklists } from './TaskChecklists';

import { TaskPod } from './TaskPod';

import { CustomField } from './EntitySettings';

import { MetricEvent } from './MetricEvent';

import { GeocodingMetadata } from './GeocodingMetadata';

import { TriggeredGeofence } from './TriggeredGeofence.js';
import type { LatLng } from './LatLng.js';
import type { TaskStatusEnumType } from 'common/enums/TaskStatus';

const translationsNamespace = 'validations';

class InternalPayments extends Payments {
    static fromJS(json: PaymentsModelType): InternalPayments {
        const state: Object = Object.assign({}, json);
        state.totalAmount = Amount.fromJS(state.totalAmount);
        state.pendingAmount = Amount.fromJS(state.pendingAmount);
        state.paymentInstruments = Immutable.List(
            state.paymentInstruments
        ).map(item => PaymentInstrument.fromJS(item));
        state.payments = Immutable.List(state.payments).map(item =>
            Payment.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get totalAmount(): Amount {
        return this._state.get('totalAmount');
    }

    get pendingAmount(): Amount {
        return this._state.get('pendingAmount');
    }

    get paymentInstruments(): Immutable.List<PaymentInstrument> {
        return this._state.get('paymentInstruments');
    }

    get payments(): Immutable.List<Payment> {
        return this._state.get('payments');
    }
}

class InternalTaskSlot extends TaskSlot {
    static fromJS(json: TaskSlotModelType): InternalTaskSlot {
        const state: Object = Object.assign({}, json);
        state.slot = VisitSlot.fromJS(state.slot);
        return new this(Immutable.Map(state));
    }

    get slot(): VisitSlot {
        return this._state.get('slot');
    }
}

export { InternalPayments as Payments, InternalTaskSlot as TaskSlot };

export const TaskStatus = {
    WAITING: 'WAITING',
    ONGOING: 'ONGOING',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED'
};

export const getColorFromTaskStatus = function (
    taskStatus,
    defaultColor = dashboardColors.darkSkyBlue
) {
    switch (taskStatus) {
        case TaskStatus.COMPLETED:
            return dashboardColors.coolGreen;
        case TaskStatus.CANCELLED:
            return dashboardColors.salmonTwo;
        case TaskStatus.WAITING:
            return dashboardColors.slate;
        default:
            return defaultColor;
    }
};

export const getNeoColorFromTaskStatus = function (
    taskStatus: TaskStatusEnumType
): string {
    switch (taskStatus) {
        case TaskStatus.COMPLETED:
            return 'green.500';
        case TaskStatus.CANCELLED:
            return 'red.400';
        case TaskStatus.WAITING:
            return 'grey.600';
        case TaskStatus.ONGOING:
            return 'blue.400';
        default:
            return 'grey.600'; // to match behavior with getTaskColor() in MarkerColorUtil.jsx
    }
};

@validatable
class InternalTaskVisit extends TaskVisit {
    static fromJS(json: TaskVisitModelType): InternalTaskVisit {
        const state: Object = Object.assign({}, json);
        state.appFields = AppFields.fromJS(state.appFields);
        state.assignedUser = state.assignedUser
            ? AssignedUser.fromJS(state.assignedUser)
            : state.assignedUser;
        state.checklists = TaskChecklists.fromJS(state.checklists);
        state.customerInput = state.customerInput
            ? SelectedChecklists.fromJS(state.customerInput)
            : state.customerInput;
        state.customFields = Immutable.Map(state.customFields);
        state.customerNotes = Immutable.List(state.customerNotes);
        state.events = state.events
            ? MetricEvent.fromJS(state.events)
            : state.events;
        state.firstAssignedUser = state.firstAssignedUser
            ? AssignedUser.fromJS(state.firstAssignedUser)
            : state.firstAssignedUser;
        state.initialSlot = TimeSlot.fromJS(state.initialSlot);
        state.locationDetails = state.locationDetails
            ? MasterLocation.fromJS(state.locationDetails)
            : state.locationDetails;
        state.pod = TaskPod.fromJS(state.pod);
        state.poc = TaskPod.fromJS(state.poc);
        state.proofOfStarted = TaskPod.fromJS(state.proofOfStarted);
        state.proofOfArrived = TaskPod.fromJS(state.proofOfArrived);
        state.proofOfTransacting = TaskPod.fromJS(state.proofOfTransacting);
        state.possibleDays = state.possibleDays
            ? Immutable.List(state.possibleDays).map(item =>
                  TourDay.fromJS(item)
              )
            : state.possibleDays;
        state.predictedTravel = state.predictedTravel
            ? TimeDistancePair.fromJS(state.predictedTravel)
            : state.predictedTravel;
        state.resources = Immutable.List(state.resources).map(item =>
            VisitResource.fromJS(item)
        );
        state.routes = Immutable.List(state.routes).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.skills = Skills.fromJS(state.skills);
        state.statusUpdates = TaskStatusUpdate.fromJS(state.statusUpdates);
        state.team = Team.fromJS(state.team);
        state.violations = Immutable.List(state.violations).map(item =>
            ConstraintViolation.fromJS(item)
        );
        state.chosenLocation = InternalChosenLocation.fromJS(
            state.chosenLocation
        );
        state.volume = InternalVolume.fromJS(state.volume);
        state.orderDetail = OrderDetail.fromJS(state.orderDetail);
        state.amountTransaction = AmountTransaction.fromJS(
            state.amountTransaction
        );
        state.payments = InternalPayments.fromJS(state.payments);
        state.locationId = state.locationId
            ? LocationId.fromJS(state.locationId)
            : state.locationId;
        state.triggeredGeofences = Immutable.List(
            state.triggeredGeofences
        ).map(item => TriggeredGeofence.fromJS(item));
        return new this(Immutable.Map(state));
    }

    getCombinedId(): string {
        return `${this.clientId}/${this.sourceId}/${this.id}`;
    }

    isHomebase(): boolean {
        return this.visitType === 'HOMEBASE';
    }

    isPickup(): boolean {
        return this.transactionType === 'PICKUP' ? true : false;
    }

    isDrop(): boolean {
        return this.transactionType === 'DROP' ? true : false;
    }

    isUserVisit(): boolean {
        return this.visitSource === 'USER';
    }

    isTaskVisit(): boolean {
        return this.visitSource === 'TASK';
    }

    getLatLng() {
        return this.chosenLocation.geometry?.latLng;
    }

    getAmountTransactionTypeText() {
        const transaction = this.amountTransaction;
        if (!transaction) {
            return 'NONE';
        }
        return transaction.getExchangeTypeText();
    }

    isSameSource(visitSource: VisitSourceEnumType): boolean {
        const UserAndTour = [VisitSourceEnum.USER, 'TOUR'];
        if (
            UserAndTour.includes(this.visitSource) &&
            UserAndTour.includes(visitSource)
        ) {
            //Temp change in backend: User and Tour are interchangable
            return true;
        }
        return this.visitSource === visitSource;
    }

    static getNewInstance(): InternalTaskVisit {
        const defaults = {
            guid: NumberUtils.guid(),
            visitType: VisitTypeEnum.Pickup,
            volume: {
                exchangeType: 'COLLECT'
            },
            isVisitTypeSelected: false
        };
        var taskVisit = InternalTaskVisit.fromJS(
            defaults
        )._setTransactionDuration(0);
        return taskVisit;
    }

    partialClone(): InternalTaskVisit {
        const taskVisit = InternalTaskVisit.getNewInstance()
            .setOrderDetail(OrderDetail.fromJS(this.orderDetail.toJS()))
            ._setVolumeValue(Number(this.volume.value));

        if (!this.chosenLocation.contact) {
            return taskVisit;
        }

        return taskVisit._setCountryCode(
            this.chosenLocation.contact.countryCode
        );
    }

    @validate
    setAddress(formattedAddress: string) {
        return this._setAddress(formattedAddress);
    }

    _setAddress(formattedAddress: string) {
        const updatedAddress = this.chosenLocation.address.setFormattedAddress(
            formattedAddress
        );
        const updateChosenLocation = this.chosenLocation.setAddress(
            updatedAddress
        );
        return this.setChosenLocation(updateChosenLocation);
    }

    validateAddress() {
        var validationMsg = null;
        if (
            DataUtils.isEmpty(this.locationId) &&
            StringUtils.isEmpty(this.chosenLocation.address.formattedAddress)
        ) {
            validationMsg = I18nHelper.translate(
                translationsNamespace,
                'emptyAddress'
            );
        }
        return validationMsg;
    }

    emptyAddress(): this {
        const updatedChosenLocation = this.chosenLocation.setAddress(
            StructuredAddress.fromJS({})
        );
        return this.setChosenLocation(updatedChosenLocation);
    }

    setShouldSkipAddressValidation(flag: boolean): this {
        return this.clone(this._state.set('shouldSkipAddressValidation', flag));
    }

    getShouldSkipAddressValidation(): boolean {
        return this._state.get('shouldSkipAddressValidation');
    }

    _setVolumeValue(volumeValue: number) {
        return this.setVolume(this.volume.setValue(volumeValue));
    }

    @validate
    setVolumeValue(volumeValue: number) {
        return this._setVolumeValue(volumeValue);
    }

    validateVolumeValue() {
        var validationMessage = null;
        if (StringUtils.isEmpty(this.volume.value)) {
            validationMessage = I18nHelper.translate(
                translationsNamespace,
                'emptyVolume'
            );
        }
        return validationMessage;
    }

    getValidationMessage(key: string): string {
        const fieldName = this.getValidatorField(key);
        return this.get(fieldName);
    }

    _setCountryCode(countryCode: string) {
        let { contact } = this.chosenLocation;
        contact = contact.setCountryCode(countryCode);
        const updatedChosenLocation = this.chosenLocation.setContact(contact);

        return this.setChosenLocation(updatedChosenLocation);
    }

    @validate
    setTransactionDuration(transactionDuration: number) {
        return this._setTransactionDuration(transactionDuration);
    }

    _setTransactionDuration(transactionDuration: number) {
        const updatedSlot = this.chosenLocation.slot.setTransactionDuration(
            transactionDuration
        );
        const updatedChosenLocation = this.chosenLocation.setSlot(updatedSlot);
        return this.setChosenLocation(updatedChosenLocation);
    }

    validateTransactionDuration(): ?string {
        var validationMessage = null;
        if (StringUtils.isEmpty(this.chosenLocation.slot.transactionDuration)) {
            validationMessage = I18nHelper.translate(
                translationsNamespace,
                'emptyTransactionTime'
            );
        }
        return validationMessage;
    }

    @validate
    setGeometry(geometry: Geometry): this {
        const updatedChosenLocation = this.chosenLocation.setGeometry(geometry);
        return this.setChosenLocation(updatedChosenLocation);
    }

    deleteGeometry(): this {
        const updatedChosenLocation = this.chosenLocation.deleteGeometry();
        return this.setChosenLocation(updatedChosenLocation);
    }

    setShouldSkipGeometryValidation(flag: boolean): this {
        return this.clone(
            this._state.set('shouldSkipGeometryValidation', flag)
        );
    }

    getShouldSkipGeometryValidation(): boolean {
        return this._state.get('shouldSkipGeometryValidation');
    }

    validatePhoneNumber(phoneNumber: ?string): ?string {
        if (phoneNumber) {
            const numberOfCharacters = phoneNumber.length;
            if (numberOfCharacters < 7) {
                return I18nHelper.translate(
                    translationsNamespace,
                    'invalidPhoneNumber'
                );
            }
            if (numberOfCharacters > 16) {
                return I18nHelper.translate(
                    translationsNamespace,
                    'phoneNumber.lengthGreaterThan16'
                );
            }
        }
    }

    @validate
    setContactNumber(contactNumber: string): this {
        let { contact } = this.chosenLocation;
        if (!contact) {
            contact = Contact.fromJS({});
        }
        const newContact = contact.setNumber(contactNumber);
        const newChosenLocation = this.chosenLocation.setContact(newContact);
        return this.setChosenLocation(newChosenLocation);
    }

    validateContactNumber(): ?string {
        const sanitizedContactNumber = this.chosenLocation.contact?.sanitize()
            .number;
        const { alternateNumber } = this.chosenLocation.contact ?? {};
        if (sanitizedContactNumber) {
            const validationMsg = this.validatePhoneNumber(
                sanitizedContactNumber
            );
            if (validationMsg) {
                return validationMsg;
            }
            return this.checkAlternateNumberEqualsPrimary();
        }
        if (alternateNumber && !sanitizedContactNumber) {
            return I18nHelper.translate(
                translationsNamespace,
                'emptyPrimaryNumber'
            );
        }
    }

    @validate
    setAlternateNumber(alternateNumber: string): this {
        let { contact } = this.chosenLocation;
        if (!contact) {
            contact = Contact.fromJS({});
        }
        const newContact = contact.setAlternateNumber(alternateNumber);
        const newChosenLocation = this.chosenLocation.setContact(newContact);
        return this.setChosenLocation(newChosenLocation);
    }

    validateAlternateNumber(): ?string {
        const { alternateNumber } = this.chosenLocation.contact ?? {};
        const { dialCode } = CountryHelper.guessDialCodeFromPhoneNumber(
            alternateNumber
        );
        const dialCodeWithPlus = `+${dialCode}`;
        const onlyPhoneNumber = alternateNumber?.replace(dialCodeWithPlus, '');
        const validationMessage = this.validatePhoneNumber(onlyPhoneNumber);

        if (validationMessage) {
            return validationMessage;
        }

        return this.checkAlternateNumberEqualsPrimary();
    }

    checkAlternateNumberEqualsPrimary(): ?string {
        const { number, countryCode, alternateNumber } =
            this.chosenLocation.contact ?? {};
        const primaryNumber = number ?? '';
        const dialCode = countryCode ?? '';
        const dialCodeWithPlus = dialCode.startsWith('+')
            ? dialCode
            : `+${dialCode}`;
        const primaryNumberWithCountryCode = primaryNumber.startsWith('+')
            ? primaryNumber
            : `${dialCodeWithPlus}${primaryNumber}`;
        const isSameAsPrimary =
            alternateNumber && alternateNumber === primaryNumberWithCountryCode;

        return isSameAsPrimary
            ? I18nHelper.translate(
                  translationsNamespace,
                  'alternateNumberEqualsPrimary'
              )
            : null;
    }

    validateGeometry(): ?string {
        var validationMessage = null;
        const { latLng } = this.chosenLocation.geometry || {};
        if (!latLng || !latLng.lat || !latLng.lng) {
            validationMessage = I18nHelper.translate(
                translationsNamespace,
                'unableToGeocode'
            );
        }
        return validationMessage;
    }

    @validate
    setSlot(slot: Object): this {
        return this._setSlot(slot);
    }

    _setSlot(slot: Object): this {
        const updatedChosenLocation = this.chosenLocation.setSlot(slot);
        return this.setChosenLocation(updatedChosenLocation);
    }

    convertSlotsToTimezone(timezone: string) {
        return this.setSlot(
            this.chosenLocation.slot.convertSlotToTimezone(timezone)
        );
    }

    setVisitType(visitType: VisitTypeEnumFlow): this {
        var exchangeType;
        var shouldResetVolumeValue = false;
        if (visitType === CreateTaskVisitTypesEnum.Drop) {
            exchangeType = 'GIVE';
        } else if (visitType === CreateTaskVisitTypesEnum.Service) {
            exchangeType = 'COLLECT';
            shouldResetVolumeValue = true;
        } else {
            exchangeType = 'COLLECT';
        }
        var updatedVolume = this.volume.setExchangeType(exchangeType);
        var updatedVisit = super
            .setVisitType(visitType)
            .setVolume(updatedVolume);
        if (shouldResetVolumeValue) {
            updatedVisit = updatedVisit.setVolumeValue(Number(0));
        }
        return updatedVisit;
    }

    setAmountExchangeType(exchangeType: ExchangeTypeEnumFlow): this {
        return this.setAmountTransaction(
            this.amountTransaction.setExchangeType(exchangeType)
        );
    }

    setAmountTransactionValue(amountValue: number): this {
        const updatedAmount = this.amountTransaction.amount.setAmount(
            amountValue
        );
        return this.setAmountTransaction(
            this.amountTransaction.setAmount(updatedAmount)
        );
    }

    _setAmountTransaction(amountTransaction: AmountTransaction): this {
        return super.setAmountTransaction(amountTransaction);
    }

    @validate
    setAmountTransaction(amountTransaction: AmountTransaction): this {
        return this._setAmountTransaction(amountTransaction);
    }

    validateAmountTransaction(): ?string {
        if (this.amountTransaction) {
            const { exchangeType } = this.amountTransaction;
            if (
                exchangeType === ExchangeTypeEnum.Collect ||
                exchangeType === ExchangeTypeEnum.Give
            ) {
                if (StringUtils.isEmpty(this.amountTransaction.amount.amount)) {
                    return I18nHelper.translate(
                        translationsNamespace,
                        'enterAmount'
                    );
                }
            }
        }
    }

    validateSlot(): ?string {
        var validationMessage = null;
        const { slot } = this.chosenLocation;
        if (
            (StringUtils.isEmpty(slot.startTime) ||
                StringUtils.isEmpty(slot.endTime)) ===
            StringUtils.isEmpty(slot.deliverNowSLA)
        ) {
            validationMessage = I18nHelper.translate(
                translationsNamespace,
                'enterSlotDetails'
            );
        }
        return validationMessage;
    }

    setIsHomebaseVisit(isHomebaseVisit: boolean): this {
        let updatedTaskVisit = super.setIsHomebaseVisit(isHomebaseVisit);
        const startOfDay = moment().startOf('day');
        const endOfDay = moment().endOf('day');
        const { slot } = updatedTaskVisit.chosenLocation;

        if (isHomebaseVisit) {
            // if slot is not defined, set it to default
            if (!(slot.startTime && slot.endTime)) {
                updatedTaskVisit = updatedTaskVisit.setSlot(
                    VisitSlot.fromJS({
                        startTime: startOfDay,
                        endTime: endOfDay
                    })
                );
            }

            return updatedTaskVisit;
        } else {
            if (
                slot.startTime === startOfDay.toISOString() &&
                slot.endTime === endOfDay.toISOString()
            ) {
                updatedTaskVisit = updatedTaskVisit.setSlot(
                    VisitSlot.fromJS({
                        transactionDuration: slot.transactionDuration
                    })
                );
            }
        }

        return updatedTaskVisit;
    }

    hasValidationErrors(): boolean {
        if (this.validateAddress()) {
            return true;
        }
        if (this.validateVolumeValue()) {
            return true;
        }
        if (this.validateTransactionDuration()) {
            return true;
        }
        if (this.validateSlot()) {
            return true;
        }
        if (
            !this.getShouldSkipGeometryValidation() &&
            this.validateGeometry()
        ) {
            return true;
        }
        if (this.validateContactNumber()) {
            return true;
        }
        if (this.validateAlternateNumber()) {
            return true;
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    getConfidenceText(): string {
        if (this.chosenLocation.geometry) {
            const { confidence } = this.chosenLocation.geometry;
            switch (confidence) {
                case ConfidenceEnum.High:
                    return I18nHelper.translate(
                        translationsNamespace,
                        'addressPlotted'
                    );
                case ConfidenceEnum.Medium:
                    return I18nHelper.translate(
                        translationsNamespace,
                        'verifyMappedLocation'
                    );
                case ConfidenceEnum.Low:
                    return I18nHelper.translate(
                        translationsNamespace,
                        'wrongMappedLocation'
                    );
                default:
                    return '';
            }
        }
        return '';
    }

    getConfidenceColor(): string {
        if (this.chosenLocation.geometry) {
            const { confidence } = this.chosenLocation.geometry;
            switch (confidence) {
                case ConfidenceEnum.High:
                    return dashboardColors.darkSkyBlue.name;
                case ConfidenceEnum.Medium:
                    return dashboardColors.salmon.name;
                case ConfidenceEnum.Low:
                    return dashboardColors.salmonTwo.name;
                default:
                    return dashboardColors.salmonTwo.name;
            }
        }
        return dashboardColors.salmonTwo.name;
    }

    isTerminal() {
        return this.visitStatus === VisitStatusEnum.Cancelled ||
            this.visitStatus === VisitStatusEnum.Completed
            ? true
            : false;
    }

    // This to get the list of fields that were not added when the visit was created
    getUnaddedFields(): Array<string> {
        const unaddedFields = [];
        if (!this.chosenLocation.contact?.name) {
            unaddedFields.push(BulkAddableVisitFieldsEnum.Name);
        }
        if (!this.chosenLocation.contact?.number) {
            unaddedFields.push(BulkAddableVisitFieldsEnum.ContactNumber);
        }
        if (!this.chosenLocation.contact?.alternateNumber) {
            unaddedFields.push(BulkAddableVisitFieldsEnum.AlternateNumber);
        }
        return unaddedFields;
    }

    // This will match list of fields added with getUnaddedFields()
    areUnaddedFieldswithValidData(): boolean {
        if (this.validateContactNumber()) {
            return false;
        }
        if (this.validateAlternateNumber()) {
            return false;
        }
        return true;
    }

    restoreUnsavedChanges(visitWithState: InternalTaskVisit): this {
        return this.setChosenLocation(
            visitWithState.chosenLocation
        ).setAppFields(visitWithState.appFields);
    }

    areVisitFieldsAdded(oldVisit: InternalTaskVisit): boolean {
        const sanitizedTaskVisit = this.sanitize();
        const { contact } = sanitizedTaskVisit.chosenLocation;
        const oldContact = oldVisit.chosenLocation.contact;
        if (contact?.name !== oldContact?.name && contact?.name) {
            return true;
        }
        if (contact?.number !== oldContact?.number && contact?.number) {
            return true;
        }
        if (
            contact?.alternateNumber !== oldContact?.alternateNumber &&
            contact?.alternateNumber
        ) {
            return true;
        }
        if (
            sanitizedTaskVisit.appFields.items.size !==
            oldVisit.appFields.items.size
        ) {
            return true;
        }
        return false;
    }

    sanitize(): this {
        let sanitizedTaskVisit = this;
        const { contact } = this.chosenLocation;
        if (contact) {
            if (!contact.name) {
                sanitizedTaskVisit = sanitizedTaskVisit.setIn(
                    ['chosenLocation', 'contact', 'name'],
                    null
                );
            }
            if (!contact.sanitize().number) {
                sanitizedTaskVisit = sanitizedTaskVisit.setIn(
                    ['chosenLocation', 'contact', 'number'],
                    null
                );
            }
            if (contact.countryCode) {
                sanitizedTaskVisit = sanitizedTaskVisit.setIn(
                    ['chosenLocation', 'contact', 'countryCode'],
                    null
                );
            }
            if (!contact.sanitize().alternateNumber) {
                sanitizedTaskVisit = sanitizedTaskVisit.setIn(
                    ['chosenLocation', 'contact', 'alternateNumber'],
                    null
                );
            }
        }
        if (this.appFields.items.size) {
            let { items } = this.appFields;
            items = items.filter(item => {
                if (
                    item &&
                    ((item.format === AppFieldEnums.Text && item.item) ||
                        item.additionalValues?.url)
                ) {
                    return true;
                }
                return false;
            });
            sanitizedTaskVisit = sanitizedTaskVisit.setIn(
                ['appFields', 'items'],
                items
            );
        }

        return sanitizedTaskVisit;
    }

    get chosenLocation(): InternalChosenLocation {
        return this._state.get('chosenLocation');
    }

    get volume(): InternalVolume {
        return this._state.get('volume');
    }

    get payments(): InternalPayments {
        return this._state.get('payments');
    }

    get visitType(): VisitTypeEnum {
        return this._state.get('visitType');
    }

    get orderDetail(): OrderDetail {
        return this._state.get('orderDetail');
    }

    get amountTransaction(): AmountTransaction {
        return this._state.get('amountTransaction');
    }

    get locationId(): ?LocationId {
        return this._state.get('locationId');
    }

    get appFields(): AppFields {
        return this._state.get('appFields');
    }

    get assignedUser(): ?AssignedUser {
        return this._state.get('assignedUser');
    }

    get checklists(): TaskChecklists {
        return this._state.get('checklists');
    }

    get events(): ?MetricEvent {
        return this._state.get('events');
    }

    get firstAssignedUser(): ?AssignedUser {
        return this._state.get('firstAssignedUser');
    }

    get initialSlot(): TimeSlot {
        return this._state.get('initialSlot');
    }

    get locationDetails(): ?MasterLocation {
        return this._state.get('locationDetails');
    }

    get possibleDays(): ?Immutable.List<TourDay> {
        return this._state.get('possibleDays');
    }

    get predictedTravel(): ?TimeDistancePair {
        return this._state.get('predictedTravel');
    }

    get resources(): Immutable.List<VisitResource> {
        return this._state.get('resources');
    }

    get routes(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('routes');
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    get statusUpdates(): TaskStatusUpdate {
        return this._state.get('statusUpdates');
    }

    get team(): Team {
        return this._state.get('team');
    }

    get violations(): Immutable.List<ConstraintViolation> {
        return this._state.get('violations');
    }

    get pod(): TaskPod {
        return this._state.get('pod');
    }

    get customFields(): CustomField {
        return this._state.get('customFields');
    }

    get visitStatus(): VisitStatusEnum {
        return this._state.get('visitStatus');
    }

    get poc(): TaskPod {
        return this._state.get('poc');
    }

    isPinOrUrlChecklistConfigured = checklistKey => {
        const checklistItems = this.checklists[checklistKey]?.items;
        if (!checklistItems) return false;
        if (checklistItems.size === 0) return false;
        return checklistItems.some(
            checklistItem =>
                checklistItem.format === PodTypeEnums.Pin ||
                checklistItem.format === PodTypeEnums.Url
        );
    };

    getCancellationChecklist() {
        return this.checklists.cancelled?.items;
    }

    getCancellationChecklistValues() {
        return this.poc.checklistValues;
    }

    getStartedChecklist() {
        return this.checklists.started?.items;
    }

    getStartedChecklistValues() {
        return this.proofOfStarted.checklistValues;
    }

    getArrivedChecklist() {
        return this.checklists.arrived?.items;
    }

    getArrivedChecklistValues() {
        return this.proofOfArrived.checklistValues;
    }

    getTransactingChecklist() {
        return this.checklists.transacting?.items;
    }

    getTransactingChecklistValues() {
        return this.proofOfTransacting.checklistValues;
    }

    getCompletedChecklist() {
        return this.checklists.completed?.items;
    }

    getCompletedChecklistValues() {
        return this.pod.checklistValues;
    }

    get visitSource(): VisitSourceEnum {
        return this._state.get('visitSource');
    }

    get breakType(): ?BreakTypeEnum {
        return this._state.get('breakType');
    }

    get userVisitType(): UserVisitTypeEnum {
        return this._state.get('userVisitType');
    }

    get latLng(): LatLng {
        return this.chosenLocation.geometry?.latLng;
    }

    get triggeredGeofences(): Immutable.List<TriggeredGeofence> {
        return this._state.get('triggeredGeofences');
    }

    get proofOfStarted(): TaskPod {
        return this._state.get('proofOfStarted');
    }

    get proofOfArrived(): TaskPod {
        return this._state.get('proofOfArrived');
    }

    get proofOfTransacting(): TaskPod {
        return this._state.get('proofOfTransacting');
    }

    get customerInput(): ?SelectedChecklists {
        return this._state.get('customerInput');
    }
}

class InternalChosenLocation extends ChosenLocation {
    static fromJS(json: ChosenLocationModelType): InternalChosenLocation {
        const state: Object = Object.assign({}, json);
        state.slot = VisitSlot.fromJS(state.slot);
        state.contact = state.contact
            ? InternalContact.fromJS(state.contact)
            : state.contact;
        state.address = StructuredAddress.fromJS(state.address);
        state.geometry = state.geometry
            ? Geometry.fromJS(state.geometry)
            : state.geometry;
        state.geocodingMetadata = GeocodingMetadata.fromJS(
            state.geocodingMetadata
        );
        state.nonAvailableSlots = Immutable.List(
            state.nonAvailableSlots
        ).map(item => InternalTaskSlot.fromJS(item));
        return new this(Immutable.Map(state));
    }

    get contact(): ?InternalContact {
        return this._state.get('contact');
    }

    get slot(): VisitSlot {
        return this._state.get('slot');
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    get geometry(): ?Geometry {
        return this._state.get('geometry');
    }

    get geocodingMetadata(): GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    get nonAvailableSlots(): Immutable.List<TaskSlot> {
        return this._state.get('nonAvailableSlots');
    }

    deleteGeometry(): this {
        return this.clone(this._state.delete('geometry'));
    }
}

class InternalContact extends Contact {
    static fromJS(json: ContactModelType): InternalContact {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    get countryCode(): string {
        return this._state.get('countryCode');
    }

    getFullNumber(): ?string {
        if (this.number) {
            return this.countryCode + this.number;
        }
    }

    sanitize(): this {
        if (!this.number) {
            return this;
        }
        const sanitizedNumber = this.number.replace(`+${this.countryCode}`, '');
        return this.setNumber(sanitizedNumber);
    }
}

class InternalVolume extends Volume {
    static fromJS(json: VolumeModelType): InternalVolume {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

export {
    InternalTaskVisit as TaskVisit,
    InternalChosenLocation as ChosenLocation,
    InternalContact as Contact,
    InternalVolume as Volume
};
